import type { FC } from 'react';
import { useFormik } from 'formik';
import { Button } from '@selfinspectionorg/ui-kit';
import styles from './UpdatePasswordForm.module.scss';
import { validationSchema } from './form/validation.schema';
import { type UpdatePasswordFormValues } from './form/formValue.type';
import { Card } from '@/components/ui';
import { PasswordInput } from '@/components/form/PasswordInput';
import { PasswordRequirements } from '@/components/form/PasswordRequirements';
import { useChangePasswordMutation } from '@/shared/api/auth';
import { getApiErrorString, isInvalidTokenError } from '@/shared/utils';

interface Props {
  /** The login-time policy-migration token authorising the change-password call. */
  changeToken: string;
  /**
   * A successful change, with the accepted new password. The login wizard updates its in-memory
   * password and silently re-logs-in.
   */
  onSuccess: (newPassword: string) => void | Promise<void>;
  /** Exit from the session-expired card (the `changeToken` was rejected as expired/already used). */
  onExpired: () => void;
  /** Voluntary exit via the "Back to login" escape hatch. */
  onBack: () => void;
}

/**
 * The forced password-change form, reached when `login` answers a valid but below-policy credential
 * with a `password_change_required` challenge. Rendered as an inline step of the login wizard (its
 * only host). The change-password response carries no adoptable tokens; where the user goes next is
 * the host's `onSuccess`.
 */
export const UpdatePasswordForm: FC<Props> = ({ changeToken, onSuccess, onExpired, onBack }) => {
  const { changePassword, isChangingPassword, changePasswordError } = useChangePasswordMutation();

  const form = useFormik<UpdatePasswordFormValues>({
    initialValues: {
      password: '',
      confirmPassword: '',
    },
    validationSchema,
    validateOnChange: false,
    onSubmit: async (values) => {
      await changePassword({ changeToken, newPassword: values.password });
      await onSuccess(values.password);
    },
  });

  // A rejected changeToken (expired/already used) is unrecoverable on this form — retrying resubmits
  // the same token — so the only way out is the host's `onExpired` exit toward a fresh challenge. A
  // policy violation stays as an inline, retryable field error.
  const isTokenRejected = isInvalidTokenError(changePasswordError);
  const apiError = changePasswordError && !isTokenRejected ? getApiErrorString(changePasswordError) : undefined;

  if (isTokenRejected) {
    return (
      <Card>
        <Card.Title
          title="Session expired"
          description="Your password-change session has expired. Please sign in again to set a new password."
        />
        <Button width="100%" onClick={onExpired}>
          Back to Login
        </Button>
      </Card>
    );
  }

  return (
    <Card className={styles.wrapper}>
      <Card.Title
        title="Update your password"
        description="Your current password no longer meets our security requirements. Set a new password to continue."
      />
      <form>
        <PasswordInput
          label="New password"
          required
          name="password"
          placeholder="Enter new password"
          value={form.values.password}
          onChange={form.handleChange}
          errored={!!form.errors.password}
          testId="new-password-input"
        />
        <PasswordRequirements value={form.values.password} />
        <PasswordInput
          label="Confirm password"
          required
          name="confirmPassword"
          placeholder="Confirm your password"
          value={form.values.confirmPassword}
          onChange={form.handleChange}
          errored={!!form.errors.confirmPassword || !!apiError}
          helperMessage={form.errors.confirmPassword || apiError}
          testId="confirm-password-input"
        />
        <div className={styles.buttonContainer}>
          <Button
            onClick={form.submitForm}
            width="100%"
            isLoading={isChangingPassword || form.isSubmitting}
            data-testid="create-password-button"
          >
            Update password
          </Button>
          {/* On the standalone page a reload keeps the changeToken (history state persists in
              window.history.state), so the user would otherwise be stuck here — this is their way out
              to re-login. Inline it abandons the challenge back to the credential step. */}
          <Button.Ghost onClick={onBack} data-testid="back-to-login-button">
            Back to login
          </Button.Ghost>
        </div>
      </form>
    </Card>
  );
};
